/**
 * The React wrapper around CodeMirror.
 *
 * The view is created exactly once. Everything that changes afterwards —
 * theme, font, wrapping, diagnostics, the list of declared function names —
 * goes in through a compartment reconfiguration or a state effect. Recreating
 * the view on a prop change is the classic mistake here: it silently discards
 * the undo history, the scroll position and the selection, which users
 * experience as the editor "resetting itself" whenever they flip a switch.
 *
 * Value flows both ways, so an echo guard is needed: when the user types we
 * notify upward and mark the change as ours, and when `value` comes back down
 * unchanged we skip the round trip.
 */
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { EditorSelection, EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import type { Diagnostic } from '../language/diagnostics/types';
import type { SourceRange } from '../language/ast/source';
import { setDiagnosticsEffect } from './diagnostics';
import {
  createEditorExtensions,
  tabSizeCompartment,
  themeCompartment,
  wrapCompartment,
} from './setup';
import { flowScriptTheme } from './theme';
import { formatDocumentCommand } from './commands';

export interface CodeEditorHandle {
  focus(): void;
  getView(): EditorView | null;
  /** Scrolls a source range into view, optionally selecting it. */
  revealRange(range: SourceRange, options?: { select?: boolean }): void;
  getSelectionOffset(): number;
  format(): boolean;
}

export interface CodeEditorProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly diagnostics: readonly Diagnostic[];
  readonly functionNames: readonly string[];
  readonly dark: boolean;
  readonly fontSize: number;
  readonly fontFamily: string;
  readonly lineWrapping: boolean;
  readonly tabSize?: number;
  readonly onCursorChange?: (offset: number) => void;
  readonly ariaLabel: string;
  readonly className?: string;
}

export const CodeEditor = forwardRef<CodeEditorHandle, CodeEditorProps>(function CodeEditor(
  {
    value,
    onChange,
    diagnostics,
    functionNames,
    dark,
    fontSize,
    fontFamily,
    lineWrapping,
    tabSize = 4,
    onCursorChange,
    ariaLabel,
    className,
  },
  ref,
) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);

  // Read through refs inside CodeMirror callbacks: the view is built once, so
  // it would otherwise close over the first render's props forever.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onCursorChangeRef = useRef(onCursorChange);
  onCursorChangeRef.current = onCursorChange;
  const functionNamesRef = useRef(functionNames);
  functionNamesRef.current = functionNames;
  const emittedValue = useRef(value);

  /* --- create the view once ------------------------------------------ */

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: value,
        extensions: [
          ...createEditorExtensions({
            dark,
            fontSize,
            fontFamily,
            lineWrapping,
            tabSize,
            getFunctionNames: () => functionNamesRef.current,
          }),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              const text = update.state.doc.toString();
              emittedValue.current = text;
              onChangeRef.current(text);
            }
            if (update.selectionSet && onCursorChangeRef.current) {
              onCursorChangeRef.current(update.state.selection.main.head);
            }
          }),
          EditorView.contentAttributes.of({ 'aria-label': ariaLabel }),
        ],
      }),
    });

    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Intentionally empty: the view is created once and reconfigured below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --- external value changes ----------------------------------------- */

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (value === emittedValue.current) return; // our own edit coming back

    const current = view.state.doc.toString();
    if (current === value) {
      emittedValue.current = value;
      return;
    }

    const head = Math.min(view.state.selection.main.head, value.length);
    emittedValue.current = value;
    view.dispatch({
      changes: { from: 0, to: current.length, insert: value },
      selection: EditorSelection.cursor(head),
    });
  }, [value]);

  /* --- reconfiguration ------------------------------------------------- */

  useEffect(() => {
    viewRef.current?.dispatch({
      effects: themeCompartment.reconfigure(flowScriptTheme({ dark, fontSize, fontFamily })),
    });
  }, [dark, fontSize, fontFamily]);

  useEffect(() => {
    viewRef.current?.dispatch({
      effects: wrapCompartment.reconfigure(lineWrapping ? EditorView.lineWrapping : []),
    });
  }, [lineWrapping]);

  useEffect(() => {
    viewRef.current?.dispatch({
      effects: tabSizeCompartment.reconfigure(EditorState.tabSize.of(tabSize)),
    });
  }, [tabSize]);

  useEffect(() => {
    viewRef.current?.dispatch({ effects: setDiagnosticsEffect.of(diagnostics) });
  }, [diagnostics]);

  /* --- imperative API --------------------------------------------------- */

  useImperativeHandle(
    ref,
    () => ({
      focus: () => viewRef.current?.focus(),
      getView: () => viewRef.current,
      getSelectionOffset: () => viewRef.current?.state.selection.main.head ?? 0,
      format: () => {
        const view = viewRef.current;
        return view ? formatDocumentCommand(view) : false;
      },
      revealRange: (range: SourceRange, options?: { select?: boolean }) => {
        const view = viewRef.current;
        if (!view) return;
        const limit = view.state.doc.length;
        const from = Math.max(0, Math.min(range.start.offset, limit));
        const to = Math.max(from, Math.min(range.end.offset, limit));
        view.dispatch({
          selection: options?.select
            ? EditorSelection.range(from, to)
            : EditorSelection.cursor(from),
          scrollIntoView: true,
        });
      },
    }),
    [],
  );

  return (
    <div
      ref={hostRef}
      className={`h-full min-h-0 w-full overflow-hidden ${className ?? ''}`}
      data-testid="code-editor"
    />
  );
});
